import { relations, sql } from 'drizzle-orm'
import {
  index,
  integer,
  pgEnum,
  pgTable,
  real,
  text,
  uniqueIndex,
} from 'drizzle-orm/pg-core'
import {
  getBaseFields,
  getBaseIndexes,
  getNullableIdReference,
  getTimestampWithTimezone,
  typedJsonb,
} from '../common/utils'
import { customers } from './customer'
import { emails } from './email'
import { formSubmissions } from './formSubmission'
import { opportunities } from './opportunity'
import { propertyTypeEnum } from './property'
import { tasks } from './task'
import { users } from './user'

export interface OpportunityPrefillData {
  user?: {
    firstName?: string
    lastName?: string
    email?: string
    phoneNumber?: string
  }
  saleType?: 'subscription' | 'single_time'
  source?: string
  notes?: string
  data?: {
    address?: {
      street?: string
      city?: string
      postalCode?: string
    }
    propertyType?: (typeof propertyTypeEnum.enumValues)[number]
  }
}

export interface AiQualityScores {
  accuracy: number
  completeness: number
  tone: number
  actionability: number
}

export interface AiClassification {
  category:
    | 'tarjouspyyntö'
    | 'soittopyyntö'
    | 'reklamaatio'
    | 'tiedustelu'
    | 'ajanvaraus'
    | 'laskutus'
    | 'muu'
    | 'roskaposti'
    // Self-generated email delivery notification (bounce / complaint alert sent
    // from our own no-reply address back into the info@ inbox). Set
    // deterministically without AI — see processAiInbox.
    | 'toimitusongelma'
    // Self-generated informational notification (e.g. "Tarjous hyväksytty",
    // "Uusi käynnin varaus") our own system sends into the info@ inbox. Not a
    // delivery problem and not a customer inquiry — set deterministically
    // without AI, see processAiInbox / classifySystemEmail.
    | 'järjestelmäilmoitus'
  extractedData: {
    name?: string
    email?: string
    phone?: string
    address?: string
    propertyType?: string
    services?: string[]
    preferredDate?: string
    notes?: string
  }
  suggestedActions: Array<{
    type:
      | 'create_opportunity'
      | 'create_task'
      | 'archive'
      | 'respond'
      | 'ask_more_info'
    label: string
    prefillData?: OpportunityPrefillData
  }>
  needsMoreInfo: boolean
  missingFields?: string[]
}

export const inboxStatusEnum = pgEnum('inbox_status', [
  'new',
  'ai_processing',
  'ready',
  'in_progress',
  'done',
])

export const inboxItems = pgTable(
  'inbox_items',
  {
    ...getBaseFields(),

    // Source — only one is non-null
    emailId: getNullableIdReference(() => emails.id),
    formSubmissionId: getNullableIdReference(() => formSubmissions.id),
    channel: text().notNull(), // 'email' | 'form'

    // Workflow
    status: inboxStatusEnum().notNull().default('new'),
    assignedToUserId: getNullableIdReference(() => users.id),
    respondedAt: getTimestampWithTimezone(),

    // When the automatic "we received your message" acknowledgment was sent.
    // Null = not yet acknowledged; doubles as the idempotency guard so the AI
    // inbox processor never sends a second receipt for the same item.
    acknowledgedAt: getTimestampWithTimezone(),

    // Linked entities
    opportunityId: getNullableIdReference(() => opportunities.id),
    customerId: getNullableIdReference(() => customers.id),
    taskId: getNullableIdReference(() => tasks.id),

    // Denormalized display fields
    senderEmail: text(),
    senderName: text(),
    subject: text(),
    preview: text(),
    receivedAt: getTimestampWithTimezone(),

    // Conversation threading
    conversationId: text(),

    // AI output
    aiRetryCount: integer().notNull().default(0),
    aiClassification: typedJsonb<AiClassification>('ai_classification'),
    aiDraftResponse: text(),
    aiDraftSubject: text(),
    aiSummary: text(),
    draftResponse: text(), // Human-edited draft, persisted between sessions
    aiConfidence: real(),
    aiProcessedAt: getTimestampWithTimezone(),
    aiModel: text(),
    aiPromptVersion: text(),

    // Human feedback
    humanAction: text(), // 'accepted' | 'modified' | 'rejected'
    humanEditedResponse: text(),
    humanActionAt: getTimestampWithTimezone(),
    humanActionUserId: getNullableIdReference(() => users.id),

    // AI quality evaluation
    aiQualityScore: real(),
    aiQualityScores: typedJsonb<AiQualityScores>('ai_quality_scores'),
    aiQualityReason: text(),
  },
  (table) => [
    ...getBaseIndexes('inbox_items', table),
    index('inbox_items_status_idx').on(table.status),
    uniqueIndex('inbox_items_email_id_unique_idx')
      .on(table.emailId)
      .where(sql`email_id IS NOT NULL`),
    uniqueIndex('inbox_items_form_submission_id_unique_idx')
      .on(table.formSubmissionId)
      .where(sql`form_submission_id IS NOT NULL`),
    index('inbox_items_conversation_id_idx').on(table.conversationId),
    index('inbox_items_received_at_idx').on(table.receivedAt),
    index('inbox_items_assigned_to_idx').on(table.assignedToUserId),
  ],
)

export const inboxItemsRelations = relations(inboxItems, ({ one }) => ({
  email: one(emails, {
    fields: [inboxItems.emailId],
    references: [emails.id],
  }),
  formSubmission: one(formSubmissions, {
    fields: [inboxItems.formSubmissionId],
    references: [formSubmissions.id],
  }),
  assignedTo: one(users, {
    fields: [inboxItems.assignedToUserId],
    references: [users.id],
    relationName: 'inboxItemAssignedTo',
  }),
  opportunity: one(opportunities, {
    fields: [inboxItems.opportunityId],
    references: [opportunities.id],
  }),
  customer: one(customers, {
    fields: [inboxItems.customerId],
    references: [customers.id],
  }),
  task: one(tasks, {
    fields: [inboxItems.taskId],
    references: [tasks.id],
  }),
  humanActionUser: one(users, {
    fields: [inboxItems.humanActionUserId],
    references: [users.id],
    relationName: 'inboxItemHumanAction',
  }),
}))
